/**
 * PATH: apps/quote/src/components/builder/panels/inspector/overrides-helpers.ts
 * FILE_ID: QB-INSPECTOR-OVERRIDES-HELPERS-001
 * PURPOSE: Pure I→O helpers for the Manual Overrides tab.
 * WHY: The OverridesTab handles two distinct number formats:
 *      (1) `adjusted_rate` is a dollar amount stored verbatim.
 *      (2) The 5 discipline overrides (markup/wastage/downtime) are stored
 *          as decimal fractions (e.g. 0.30 = 30%) but displayed and edited
 *          as whole percentages so estimators don't have to think in
 *          decimals. These helpers centralise the parse + format round-trip
 *          so every input control behaves identically.
 *
 *      Extracted from OverridesTab.tsx to keep that file under the 300-line
 *      hard limit and to make the conversions independently testable.
 *
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 *   │ stored decimal│ → │ percentText      │ → │ rendered in <input> │
 *   │ e.g. "0.3000" │   │ "30"             │   │ value="30"          │
 *   └───────────────┘   └──────────────────┘   └─────────────────────┘
 *   ┌───────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 *   │ user types    │ → │ parsePercentInput│ → │ patch {field: 0.30} │
 *   │ "30"          │   │  → 0.30          │   │ → onUpdate          │
 *   └───────────────┘   └──────────────────┘   └─────────────────────┘
 *
 * DEPENDENCIES: none (pure functions; deliberately framework-free).
 * RELATED:
 *   - OverridesTab.tsx — the only consumer today.
 */

/** Rounds to the given number of decimal places without floating-point trail. */
function round(value: number, places: number): number {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

/** Trimmed string form of a number-or-string-or-null value, or '' if empty. */
export function toFieldText(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

/**
 * Convert a stored decimal fraction (e.g. 0.30) into the whole-number
 * percentage string an estimator types (e.g. "30"). Returns '' for null /
 * undefined / empty so the input remains placeholder-driven.
 *
 * We use Number.parseFloat for resilience against backend Decimal strings
 * like "0.3000" — they come through the DRF DecimalField serializer as
 * trailing-zero strings.
 */
export function decimalToPercentText(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return ''
  const n = Number.parseFloat(String(value))
  if (!Number.isFinite(n)) return ''
  // Use 4 decimal places on the percent side because the backend column
  // is numeric(5,4) — 100% = 1.0000 = "100" with no fractional part, but
  // 7.5% = 0.0750 = "7.5".
  const pct = round(n * 100, 2)
  return String(pct)
}

/**
 * Parse a percentage input string into a decimal fraction (e.g. "30" → 0.30,
 * "7.5" → 0.075).
 *
 * Return values:
 *   - null      → blank input means "clear the override"
 *   - number    → valid percent, converted to decimal
 *   - undefined → invalid input (caller should reject and revert)
 *
 * Negative percentages are accepted because a backend
 * numeric(5,4) field allows e.g. a discount markup; clamping is the
 * caller's responsibility if business rules forbid it.
 */
export function parsePercentInput(raw: string): number | null | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number.parseFloat(trimmed)
  if (!Number.isFinite(n)) return undefined
  return round(n / 100, 6)
}

/**
 * Parse a raw numeric input string (no percent conversion). Used by
 * `adjusted_rate` and any future override that stores a literal value.
 */
export function parseNumericInput(raw: string): number | null | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number.parseFloat(trimmed)
  if (!Number.isFinite(n)) return undefined
  return n
}

/**
 * Resolve the placeholder shown beneath a markup input — the quote config
 * value if set, otherwise the engine's hard-coded default. Mirrors the
 * backend's ``CalculationEngine._resolve`` precedence sans the per-line
 * tier (which is the value currently in the input).
 *
 * Engine defaults are duplicated here intentionally: they're the same
 * numbers the backend uses (services/calculation_engine.py:107) and they
 * change rarely. Tests in ``overrides-helpers.test.ts`` pin both sides.
 */
export const ENGINE_DEFAULTS = {
  materialMarkup: 0.5,
  materialWastage: 0.1,
  plantMarkup: 0.2,
  subcontractorMarkup: 0.15,
  downtimeFactor: 0.1,
} as const

export type DisciplineConfigKey = keyof typeof ENGINE_DEFAULTS

export function resolveConfigDefault(
  config: Record<string, unknown> | null | undefined,
  key: DisciplineConfigKey,
): number {
  const fromConfig = config?.[key]
  if (typeof fromConfig === 'number' && Number.isFinite(fromConfig)) {
    return fromConfig
  }
  if (typeof fromConfig === 'string' && fromConfig.trim() !== '') {
    const n = Number.parseFloat(fromConfig)
    if (Number.isFinite(n)) return n
  }
  return ENGINE_DEFAULTS[key]
}

/** Formats a decimal-fraction default value as a "(default: 50%)" string. */
export function formatDefaultLabel(decimal: number): string {
  const pct = round(decimal * 100, 2)
  return `default: ${pct}%`
}
